import { useState, useEffect } from 'react';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { supabase } from '@/integrations/supabase/client';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { 
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { toast } from 'sonner';
import { 
  CreditCard, 
  Calendar, 
  TrendingUp, 
  FileText, 
  ExternalLink,
  CheckCircle,
  AlertCircle,
  ArrowUp,
  ArrowDown,
  Download,
  Receipt,
  Loader2,
  Crown,
  Settings
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';

interface Invoice {
  id: string;
  invoice_number: string;
  amount: number;
  plan: string;
  billing_period_start: string;
  billing_period_end: string;
  status: string;
  created_at: string;
  paid_at: string | null;
}

interface BillingInfo {
  plan: string;
  sessionQuota: number;
  sessionsUsed: number;
  billingCycle: 'monthly' | 'yearly';
  nextBillingDate: string;
  amount: number;
  customPriceOverride: number | null;
  priceOverrideNotes: string | null;
}

// Color mapping for plan badges
const PLAN_COLORS: Record<string, string> = {
  free: 'bg-gray-100 text-gray-800',
  startup: 'bg-blue-100 text-blue-800',
  mid_level: 'bg-green-100 text-green-800',
  pro: 'bg-indigo-100 text-indigo-800',
  scale: 'bg-orange-100 text-orange-800',
  enterprise: 'bg-purple-100 text-purple-800',
};

export default function AdminBillingPage() {
  const { user } = useAdminAuth();
  const [billingInfo, setBillingInfo] = useState<BillingInfo>({
    plan: 'free',
    sessionQuota: 100,
    sessionsUsed: 0,
    billingCycle: 'monthly',
    nextBillingDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
    amount: 0,
    customPriceOverride: null,
    priceOverrideNotes: null
  });
  const [loading, setLoading] = useState(true);
  const [changePlanDialogOpen, setChangePlanDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [changingPlan, setChangingPlan] = useState(false);
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [loadingInvoices, setLoadingInvoices] = useState(false);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const [invoiceDialogOpen, setInvoiceDialogOpen] = useState(false);
  

  // Database plans state
  const [dbPlans, setDbPlans] = useState<Array<{
    id: string;
    plan_key: string;
    name: string;
    price: number;
    patient_limit: number | null;
    supervisor_limit: number | null;
    features: string[];
    is_active: boolean;
    sort_order: number;
  }>>([]);
  const [loadingPlans, setLoadingPlans] = useState(true);

  const isSuperAdmin = user?.role === 'super_admin';
  const isAgencyAdmin = user?.role === 'agency_admin';

  useEffect(() => {
    loadDbPlans();
    loadInvoices();
  }, [user]);

  // Reload billing info after plans are loaded
  useEffect(() => {
    if (!loadingPlans && dbPlans.length > 0) {
      loadBillingInfo();
    }
  }, [loadingPlans, dbPlans.length, user]);

  const loadDbPlans = async () => {
    try {
      const { data, error } = await supabase
        .from('subscription_plans')
        .select('id, plan_key, name, price, patient_limit, supervisor_limit, features, is_active, sort_order')
        .eq('is_active', true)
        .order('sort_order', { ascending: true });

      if (error) throw error;
      setDbPlans((data || []).map(plan => ({
        ...plan,
        features: Array.isArray(plan.features) ? plan.features as string[] : []
      })));
    } catch (error) {
      console.error('Error loading plans:', error);
    } finally {
      setLoadingPlans(false);
    }
  };

  const loadBillingInfo = async () => {
    if (!user?.agency_id) {
      setLoading(false);
      return;
    }

    try {
      // Get agency details including custom pricing fields
      const { data: agency } = await supabase
        .from('agencies')
        .select('plan, custom_price_override, price_override_notes')
        .eq('id', user.agency_id)
        .single();

      // Get current month's session count
      const startOfMonth = new Date();
      startOfMonth.setDate(1);
      startOfMonth.setHours(0, 0, 0, 0);

      const { count: sessionsUsed } = await supabase
        .from('triage_sessions')
        .select('*', { count: 'exact', head: true })
        .eq('agency_id', user.agency_id)
        .gte('created_at', startOfMonth.toISOString());

      if (agency) {
        const planKey = agency.plan as string;
        const customPrice = agency.custom_price_override as number | null;
        
        // Get plan details from database plans if available
        const dbPlan = dbPlans.find(p => p.plan_key === planKey);
        const planPrice = dbPlan?.price || 0;
        const patientLimit = dbPlan?.patient_limit || 100;
        
        setBillingInfo({
          plan: planKey,
          sessionQuota: patientLimit,
          sessionsUsed: sessionsUsed || 0,
          billingCycle: 'monthly',
          nextBillingDate: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
          amount: customPrice !== null ? customPrice : planPrice,
          customPriceOverride: customPrice,
          priceOverrideNotes: agency.price_override_notes as string | null
        });
      }
    } catch (error) {
      console.error('Error loading billing info:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadInvoices = async () => {
    if (!user?.agency_id) return;
    
    setLoadingInvoices(true);
    try {
      const { data, error } = await supabase
        .from('invoices')
        .select('*')
        .eq('agency_id', user.agency_id)
        .order('created_at', { ascending: false })
        .limit(10);

      if (error) throw error;
      setInvoices((data as Invoice[]) || []);
    } catch (error) {
      console.error('Error loading invoices:', error);
    } finally {
      setLoadingInvoices(false);
    }
  };

  const handleGenerateInvoice = async () => {
    if (!user?.agency_id || billingInfo.plan === 'free') return;
    
    setGeneratingInvoice(true);
    try {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);

      const { data, error } = await supabase.functions.invoke('generate-invoice', {
        body: {
          agencyId: user.agency_id,
          plan: billingInfo.plan,
          amount: billingInfo.amount,
          billingPeriodStart: startOfMonth.toISOString().split('T')[0],
          billingPeriodEnd: endOfMonth.toISOString().split('T')[0]
        }
      });

      if (error) throw error;

      toast.success(`Invoice ${data.invoiceNumber} generated successfully`);
      await loadInvoices();
    } catch (error) {
      console.error('Error generating invoice:', error);
      toast.error('Failed to generate invoice');
    } finally {
      setGeneratingInvoice(false);
    }
  };

  const downloadInvoice = (invoice: Invoice) => {
    const agencyName = user?.agency?.name || 'Agency';
    const invoiceHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <title>Invoice ${invoice.invoice_number}</title>
        <style>
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
          .header { display: flex; justify-content: space-between; margin-bottom: 40px; }
          .logo { font-size: 24px; font-weight: bold; color: #6B46C1; }
          .invoice-info { text-align: right; }
          .invoice-number { font-size: 20px; font-weight: bold; }
          .details { margin-bottom: 40px; }
          .details h2 { font-size: 16px; color: #666; margin-bottom: 10px; }
          .table { width: 100%; border-collapse: collapse; margin-bottom: 40px; }
          .table th, .table td { padding: 12px; text-align: left; border-bottom: 1px solid #eee; }
          .table th { background: #f9fafb; font-weight: 600; }
          .total-row { font-weight: bold; font-size: 18px; }
          .footer { margin-top: 60px; padding-top: 20px; border-top: 1px solid #eee; color: #666; font-size: 14px; }
          .paid-badge { background: #10B981; color: white; padding: 4px 12px; border-radius: 4px; font-size: 12px; }
        </style>
      </head>
      <body>
        <div class="header">
          <div class="logo">CheckFirst</div>
          <div class="invoice-info">
            <div class="invoice-number">${invoice.invoice_number}</div>
            <div style="color: #666; margin-top: 5px;">Date: ${new Date(invoice.created_at).toLocaleDateString()}</div>
            <div style="margin-top: 10px;"><span class="paid-badge">${invoice.status.toUpperCase()}</span></div>
          </div>
        </div>
        
        <div class="details">
          <h2>Bill To:</h2>
          <div style="font-size: 18px; font-weight: 600;">${agencyName}</div>
        </div>
        
        <table class="table">
          <thead>
            <tr>
              <th>Description</th>
              <th>Billing Period</th>
              <th style="text-align: right;">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>${dbPlans.find(p => p.plan_key === invoice.plan)?.name || invoice.plan} Plan Subscription</td>
              <td>${new Date(invoice.billing_period_start).toLocaleDateString()} - ${new Date(invoice.billing_period_end).toLocaleDateString()}</td>
              <td style="text-align: right;">$${Number(invoice.amount).toFixed(2)}</td>
            </tr>
            <tr class="total-row">
              <td colspan="2">Total</td>
              <td style="text-align: right;">$${Number(invoice.amount).toFixed(2)}</td>
            </tr>
          </tbody>
        </table>
        
        <div class="footer">
          <p>Thank you for your business!</p>
          <p>CheckFirst - Healthcare Triage Solutions</p>
        </div>
      </body>
      </html>
    `;

    const blob = new Blob([invoiceHtml], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${invoice.invoice_number}.html`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.success('Invoice downloaded. Open in browser and print to PDF for best results.');
  };

  const handleChangePlan = async () => {
    if (!selectedPlan || !user?.agency_id || !user?.id) return;
    
    setChangingPlan(true);
    try {
      const oldPlan = billingInfo.plan;
      const upgrading = isUpgrade(selectedPlan);
      
      const oldPlanData = dbPlans.find(p => p.plan_key === oldPlan);
      const newPlanData = dbPlans.find(p => p.plan_key === selectedPlan);
      
      // Update the agency plan
      const { error: updateError } = await supabase
        .from('agencies')
        .update({ plan: selectedPlan as any })
        .eq('id', user.agency_id);

      if (updateError) throw updateError;

      // Log the plan change in audit logs
      const { error: auditError } = await supabase
        .from('admin_audit_logs')
        .insert({
          actor_user_id: user.id,
          action: 'plan_changed',
          target_resource: 'agency',
          target_id: user.agency_id,
          metadata: {
            old_plan: oldPlan,
            new_plan: selectedPlan,
            old_price: oldPlanData?.price || 0,
            new_price: newPlanData?.price || 0
          }
        });

      if (auditError) {
        console.error('Failed to log audit:', auditError);
      }

      // Send email notification (don't block on this)
      const agencyName = user.agency?.name || 'Your Agency';
      supabase.functions.invoke('send-plan-change-notification', {
        body: {
          email: user.email,
          agencyName,
          oldPlan,
          newPlan: selectedPlan,
          oldPrice: oldPlanData?.price || 0,
          newPrice: newPlanData?.price || 0,
          oldQuota: oldPlanData?.patient_limit || 0,
          newQuota: newPlanData?.patient_limit || 0,
          isUpgrade: upgrading
        }
      }).then(({ error }) => {
        if (error) {
          console.error('Failed to send plan change email:', error);
        } else {
          console.log('Plan change notification email sent');
        }
      });

      toast.success(`Plan changed from ${oldPlanData?.name || oldPlan} to ${newPlanData?.name || selectedPlan}`);
      setChangePlanDialogOpen(false);
      setSelectedPlan(null);
      await loadBillingInfo();
    } catch (error) {
      console.error('Error changing plan:', error);
      toast.error('Failed to change plan. Please try again.');
    } finally {
      setChangingPlan(false);
    }
  };


  const isUpgrade = (newPlan: string) => {
    const currentIndex = dbPlans.findIndex(p => p.plan_key === billingInfo.plan);
    const newIndex = dbPlans.findIndex(p => p.plan_key === newPlan);
    return newIndex > currentIndex;
  };

  const getPlanColor = (plan: string) => {
    return PLAN_COLORS[plan] || 'bg-gray-100 text-gray-800';
  };

  const getPlanDisplayName = (plan: string) => {
    const dbPlan = dbPlans.find(p => p.plan_key === plan);
    return dbPlan?.name || plan.charAt(0).toUpperCase() + plan.slice(1);
  };

  const getPlanFeatures = (plan: string) => {
    const dbPlan = dbPlans.find(p => p.plan_key === plan);
    return dbPlan?.features || [];
  };


  const usagePercentage = (billingInfo.sessionsUsed / billingInfo.sessionQuota) * 100;
  const isNearLimit = usagePercentage >= 80;

  if (loading) {
    return (
      <AdminLayout>
        <div className="space-y-6">
          <div className="animate-pulse space-y-4">
            <div className="h-8 bg-muted rounded w-1/4"></div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {[...Array(3)].map((_, i) => (
                <div key={i} className="h-32 bg-muted rounded-lg"></div>
              ))}
            </div>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex flex-col space-y-2">
            <h1 className="text-3xl font-bold text-foreground">Billing & Usage</h1>
            <p className="text-muted-foreground">
              Manage your subscription and monitor usage
            </p>
          </div>
          {isSuperAdmin && (
            <Link to="/admin/settings?tab=plans">
              <Button className="gap-2 bg-purple-600 hover:bg-purple-700">
                <Crown className="h-4 w-4" />
                Manage Plans
              </Button>
            </Link>
          )}
        </div>

        {/* Current Plan */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <Card className="lg:col-span-2 shadow-card">
            <CardHeader>
              <div className="flex items-center justify-between">
                <div>
                  <CardTitle className="flex items-center space-x-2">
                    <CreditCard className="h-5 w-5" />
                    <span>Current Plan</span>
                  </CardTitle>
                  <CardDescription>
                    Your subscription details and usage
                  </CardDescription>
                </div>
                <Badge className={getPlanColor(billingInfo.plan)}>
                  {getPlanDisplayName(billingInfo.plan)}
                </Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Usage Progress */}
              <div className="space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Triage Sessions Used</span>
                  <span className="font-medium text-foreground">
                    {billingInfo.sessionsUsed.toLocaleString()} / {billingInfo.sessionQuota.toLocaleString()}
                  </span>
                </div>
                <Progress 
                  value={usagePercentage} 
                  className={`h-2 ${isNearLimit ? 'bg-red-100' : 'bg-muted'}`}
                />
                {isNearLimit && (
                  <div className="flex items-center space-x-1 text-sm text-red-600">
                    <AlertCircle className="h-4 w-4" />
                    <span>Approaching monthly limit</span>
                  </div>
                )}
              </div>

              {/* Plan Features */}
              <div>
                <h3 className="font-medium text-foreground mb-3">Plan Features</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {getPlanFeatures(billingInfo.plan).map((feature, index) => (
                    <div key={index} className="flex items-center space-x-2 text-sm">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-muted-foreground">{feature}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* Billing Info */}
              {billingInfo.plan !== 'free' && (
                <div className="pt-4 border-t border-border">
                  <div className="flex justify-between items-center text-sm">
                    <span className="text-muted-foreground">Next billing date</span>
                    <span className="font-medium text-foreground">
                      {new Date(billingInfo.nextBillingDate).toLocaleDateString()}
                    </span>
                  </div>
                  <div className="flex justify-between items-center text-sm mt-2">
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">Amount</span>
                      {billingInfo.customPriceOverride !== null && (
                        <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-800">
                          Custom
                        </Badge>
                      )}
                    </div>
                    <span className="font-medium text-foreground">
                      ${billingInfo.amount}/{billingInfo.billingCycle.replace('ly', '')}
                    </span>
                  </div>
                  {billingInfo.priceOverrideNotes && (
                    <p className="text-xs text-muted-foreground mt-2 italic">
                      Note: {billingInfo.priceOverrideNotes}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Quick Stats */}
          <div className="space-y-4">
            <Card className="shadow-card">
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <FileText className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="text-2xl font-bold text-foreground">
                      {billingInfo.sessionsUsed.toLocaleString()}
                    </p>
                    <p className="text-sm text-muted-foreground">Sessions This Month</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-card">
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <TrendingUp className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="text-2xl font-bold text-foreground">
                      {Math.round(usagePercentage)}%
                    </p>
                    <p className="text-sm text-muted-foreground">Quota Used</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="shadow-card">
              <CardContent className="p-6">
                <div className="flex items-center space-x-2">
                  <Calendar className="h-5 w-5 text-purple-600" />
                  <div>
                    <p className="text-2xl font-bold text-foreground">
                      {Math.ceil((new Date(billingInfo.nextBillingDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24))}
                    </p>
                    <p className="text-sm text-muted-foreground">Days Until Reset</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Actions */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <Card className="shadow-card">
            <CardHeader>
              <CardTitle>Manage Subscription</CardTitle>
              <CardDescription>
                Update your billing information and subscription
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  {billingInfo.plan === 'free' 
                    ? 'Upgrade to unlock more features and higher session limits.'
                    : 'Change your plan or manage your billing settings.'}
                </p>
                <Button 
                  className="w-full" 
                  onClick={() => setChangePlanDialogOpen(true)}
                >
                  Change Plan
                  <ExternalLink className="ml-2 h-4 w-4" />
                </Button>
                {billingInfo.plan !== 'free' && (
                  <Button 
                    variant="outline" 
                    className="w-full"
                    onClick={() => setInvoiceDialogOpen(true)}
                  >
                    <Receipt className="mr-2 h-4 w-4" />
                    View Invoices
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          <Card className="shadow-card">
            <CardHeader>
              <CardTitle>Usage Analytics</CardTitle>
              <CardDescription>
                Detailed breakdown of your usage patterns
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-3">
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Average daily sessions</span>
                  <span className="font-medium text-foreground">
                    {Math.round(billingInfo.sessionsUsed / Math.max(new Date().getDate(), 1))}
                  </span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Peak usage day</span>
                  <span className="font-medium text-foreground">Monday</span>
                </div>
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Projected month-end</span>
                  <span className="font-medium text-foreground">
                    {Math.round(billingInfo.sessionsUsed * (30 / Math.max(new Date().getDate(), 1)))} sessions
                  </span>
                </div>
              </div>
              <Button variant="outline" className="w-full mt-4">
                View Detailed Analytics
              </Button>
            </CardContent>
          </Card>

        </div>

        {/* Plan Pricing Summary */}
        <Card className="shadow-card">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Crown className="h-5 w-5 text-primary" />
                  Available Plans
                </CardTitle>
                <CardDescription>
                  Current pricing for all subscription tiers
                </CardDescription>
              </div>
              {isSuperAdmin && (
                <Link to="/admin/settings?tab=plans">
                  <Button variant="outline" size="sm" className="gap-1">
                    <Settings className="h-3 w-3" />
                    Edit Plans
                  </Button>
                </Link>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {loadingPlans ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : dbPlans.length === 0 ? (
              <p className="text-center text-muted-foreground py-4">No plans configured</p>
            ) : (
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {dbPlans.map((plan) => {
                  const isCurrentPlan = plan.plan_key === billingInfo.plan;
                  return (
                    <div 
                      key={plan.id} 
                      className={`relative p-4 rounded-lg border transition-all ${
                        isCurrentPlan 
                          ? 'border-primary bg-primary/5 ring-2 ring-primary/20' 
                          : 'border-border hover:border-primary/50'
                      }`}
                    >
                      {isCurrentPlan && (
                        <Badge className="absolute -top-2 left-1/2 -translate-x-1/2 text-xs">
                          Current
                        </Badge>
                      )}
                      <div className="text-center space-y-2">
                        <h3 className="font-semibold text-foreground">{plan.name}</h3>
                        <div className="flex items-baseline justify-center gap-1">
                          <span className="text-2xl font-bold text-foreground">${plan.price}</span>
                          <span className="text-xs text-muted-foreground">/mo</span>
                        </div>
                        <div className="text-xs text-muted-foreground space-y-1">
                          <p>
                            {plan.patient_limit !== null 
                              ? `${plan.patient_limit.toLocaleString()} patients`
                              : 'Unlimited patients'
                            }
                          </p>
                          <p>
                            {plan.supervisor_limit !== null 
                              ? `${plan.supervisor_limit} supervisors`
                              : 'Unlimited supervisors'
                            }
                          </p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Invoice History Dialog */}
        <Dialog open={invoiceDialogOpen} onOpenChange={setInvoiceDialogOpen}>
          <DialogContent className="sm:max-w-3xl">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Receipt className="h-5 w-5" />
                Invoice History
              </DialogTitle>
              <DialogDescription>
                View and download your billing invoices
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4">
              {billingInfo.plan !== 'free' && (
                <div className="flex justify-end">
                  <Button 
                    onClick={handleGenerateInvoice}
                    disabled={generatingInvoice}
                    size="sm"
                  >
                    {generatingInvoice ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Generating...
                      </>
                    ) : (
                      <>
                        <FileText className="mr-2 h-4 w-4" />
                        Generate Current Month Invoice
                      </>
                    )}
                  </Button>
                </div>
              )}

              {loadingInvoices ? (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
              ) : invoices.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Receipt className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No invoices yet</p>
                  <p className="text-sm">Generate your first invoice to see it here</p>
                </div>
              ) : (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Invoice #</TableHead>
                        <TableHead>Date</TableHead>
                        <TableHead>Plan</TableHead>
                        <TableHead>Amount</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {invoices.map((invoice) => (
                        <TableRow key={invoice.id}>
                          <TableCell className="font-medium">{invoice.invoice_number}</TableCell>
                          <TableCell>{new Date(invoice.created_at).toLocaleDateString()}</TableCell>
                          <TableCell>
                            <Badge className={getPlanColor(invoice.plan)}>
                              {getPlanDisplayName(invoice.plan)}
                            </Badge>
                          </TableCell>
                          <TableCell>${Number(invoice.amount).toFixed(2)}</TableCell>
                          <TableCell>
                            <Badge variant={invoice.status === 'paid' ? 'default' : 'secondary'}>
                              {invoice.status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => downloadInvoice(invoice)}
                            >
                              <Download className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setInvoiceDialogOpen(false)}>
                Close
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Change Plan Dialog */}
        <Dialog open={changePlanDialogOpen} onOpenChange={setChangePlanDialogOpen}>
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>Change Subscription Plan</DialogTitle>
              <DialogDescription>
                Select a new plan for your agency. Changes take effect immediately.
              </DialogDescription>
            </DialogHeader>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 py-4 max-h-96 overflow-y-auto">
              {dbPlans.map((plan) => {
                const isCurrent = plan.plan_key === billingInfo.plan;
                const isSelected = plan.plan_key === selectedPlan;
                const upgrading = isUpgrade(plan.plan_key);
                
                return (
                  <div
                    key={plan.id}
                    onClick={() => !isCurrent && setSelectedPlan(plan.plan_key)}
                    className={`
                      relative p-4 rounded-lg border-2 cursor-pointer transition-all
                      ${isCurrent 
                        ? 'border-primary bg-primary/5 cursor-default' 
                        : isSelected 
                          ? 'border-primary bg-primary/10' 
                          : 'border-border hover:border-primary/50'}
                    `}
                  >
                    {isCurrent && (
                      <Badge className="absolute -top-2 -right-2 bg-primary text-primary-foreground">
                        Current
                      </Badge>
                    )}
                    
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-semibold text-foreground">{plan.name}</h3>
                    </div>
                    
                    <Badge className={getPlanColor(plan.plan_key) + ' mb-2'}>
                      {plan.patient_limit ? `${plan.patient_limit.toLocaleString()} patients` : 'Unlimited'}
                    </Badge>
                    
                    <div className="flex items-baseline gap-1 mb-3">
                      <span className="text-2xl font-bold text-foreground">${plan.price}</span>
                      <span className="text-muted-foreground">/month</span>
                    </div>
                    
                    {!isCurrent && (
                      <div className="flex items-center gap-1 text-sm">
                        {upgrading ? (
                          <>
                            <ArrowUp className="h-4 w-4 text-green-600" />
                            <span className="text-green-600">Upgrade</span>
                          </>
                        ) : (
                          <>
                            <ArrowDown className="h-4 w-4 text-orange-600" />
                            <span className="text-orange-600">Downgrade</span>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>

            {selectedPlan && (() => {
              const selectedPlanData = dbPlans.find(p => p.plan_key === selectedPlan);
              const patientLimitText = selectedPlanData?.patient_limit 
                ? selectedPlanData.patient_limit.toLocaleString() 
                : 'Unlimited';
              return (
                <div className="bg-muted p-4 rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    {isUpgrade(selectedPlan) ? (
                      <>
                        <strong>Upgrading</strong> from {getPlanDisplayName(billingInfo.plan)} to {getPlanDisplayName(selectedPlan)}.
                        Your new patient limit will be {patientLimitText} per month.
                      </>
                    ) : (
                      <>
                        <strong>Downgrading</strong> from {getPlanDisplayName(billingInfo.plan)} to {getPlanDisplayName(selectedPlan)}.
                        Your patient limit will decrease to {patientLimitText} per month.
                      </>
                    )}
                  </p>
                </div>
              );
            })()}

            <DialogFooter>
              <Button variant="outline" onClick={() => {
                setChangePlanDialogOpen(false);
                setSelectedPlan(null);
              }}>
                Cancel
              </Button>
              <Button 
                onClick={handleChangePlan} 
                disabled={!selectedPlan || changingPlan}
              >
                {changingPlan ? 'Changing...' : 'Confirm Change'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </div>
    </AdminLayout>
  );
}